import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, HandCoins, PackageSearch, ReceiptText, ShoppingCart, Wallet } from "lucide-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { PosService, RestaurantSettlementService } from "../../services";
import { restaurantOrderApi } from "../../api/restaurantOrderApi";
import { useAuth } from "../../store/AuthContext";
import { businessDateLabel, currentBusinessDate } from "../../utils/businessDay";
import { currency, dateTime } from "../../utils/format";

const rowsFrom = (value) => Array.isArray(value) ? value : value?.content || value?.items || [];

export const ResCashierDashboardPage = () => {
  const { user } = useAuth();
  const [selectedBusinessDate, setSelectedBusinessDate] = useState(currentBusinessDate());
  const [sales, setSales] = useState([]);
  const [session, setSession] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErrors({});

    Promise.allSettled([
      PosService.mySales(user?.userId, selectedBusinessDate),
      PosService.cashCurrent(user?.userId),
      RestaurantSettlementService.current(selectedBusinessDate),
      RestaurantSettlementService.list(selectedBusinessDate),
      restaurantOrderApi.list({ page: 0, size: 5, fromDate: selectedBusinessDate, toDate: selectedBusinessDate }),
    ]).then(([salesResult, sessionResult, summaryResult, settlementsResult, ordersResult]) => {
      if (!active) return;

      const nextErrors = {};
      if (salesResult.status === "fulfilled") {
        setSales(rowsFrom(salesResult.value));
      } else {
        setSales([]);
        nextErrors.sales = salesResult.reason?.response?.data?.message || "Restaurant sales could not be loaded.";
      }

      if (sessionResult.status === "fulfilled") {
        setSession(sessionResult.value || null);
      } else {
        setSession(null);
      }

      if (summaryResult.status === "fulfilled") {
        setSettlementSummary(summaryResult.value || null);
      } else {
        setSettlementSummary(null);
        nextErrors.summary = summaryResult.reason?.response?.data?.message || "Settlement summary could not be loaded.";
      }

      if (settlementsResult.status === "fulfilled") {
        setSettlements(rowsFrom(settlementsResult.value));
      } else {
        setSettlements([]);
        nextErrors.settlements = settlementsResult.reason?.response?.data?.message || "BAR transfer requests could not be loaded.";
      }

      if (ordersResult.status === "fulfilled") {
        const page = ordersResult.value;
        setOrders(rowsFrom(page));
        setOrderTotal(Number(page?.totalElements ?? rowsFrom(page).length));
      } else {
        setOrders([]);
        setOrderTotal(0);
        nextErrors.orders = ordersResult.reason?.response?.data?.message || "Restaurant Orders could not be loaded.";
      }

      setErrors(nextErrors);
      setLoading(false);
    });

    return () => { active = false; };
  }, [user?.userId, selectedBusinessDate]);

  const completedSales = useMemo(
    () => sales.filter((sale) => sale.status === "COMPLETED" && sale.outletCode === "RESTAURANT"),
    [sales]
  );

  const salesTotals = useMemo(() => completedSales.reduce((totals, sale) => {
    totals.total += Number(sale.grandTotalAmount || 0);
    (Array.isArray(sale.payments) ? sale.payments : []).forEach((payment) => {
      const amount = Number(payment.amount || 0);
      if (payment.paymentMethod === "CASH") totals.cash += amount;
      if (payment.paymentMethod === "CARD") totals.card += amount;
    });
    return totals;
  }, { total: 0, cash: 0, card: 0 }), [completedSales]);

  const productRows = useMemo(() => {
    const products = new Map();
    completedSales.forEach((sale) => {
      (Array.isArray(sale.items) ? sale.items : []).forEach((item) => {
        if (!item || item.itemType === "BOTTLE") return;
        const key = item.itemId ?? item.itemName ?? item.saleItemId;
        const product = products.get(key) || { key, name: item.itemName || "Restaurant item", quantity: 0 };
        product.quantity += Number(item.quantity || 0);
        products.set(key, product);
      });
    });
    return Array.from(products.values()).sort((a, b) => b.quantity - a.quantity).slice(0, 8);
  }, [completedSales]);

  const dateLabel = businessDateLabel(selectedBusinessDate);
  const pendingRequests = settlements.filter((item) =>
    item.status === "PENDING" && String(item.businessDate || "").slice(0, 10) === selectedBusinessDate
  );
  const pendingAmount = pendingRequests.reduce((sum, item) => sum + Number(item.calculatedAmount || 0), 0);
  const firstName = user?.fullName?.trim().split(/\s+/)[0] || user?.username || "Cashier";

  return (
    <div>
      <PageHeader
        title={`Hi, ${firstName} 👋`}
        subtitle={`Restaurant snapshot for ${dateLabel}.`}
        actions={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <input
                type="date"
                value={selectedBusinessDate}
                onChange={(event) => setSelectedBusinessDate(event.target.value)}
                className="h-8 min-w-[145px] bg-transparent text-sm text-slate-800 outline-none"
                aria-label="Business date"
              />
            </div>
            <Link to="/res-cashier/pos" className="bms-btn-accent inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm">
              <ShoppingCart className="h-4 w-4" />New Sale
            </Link>
            <Link to="/res-cashier/cash-session" className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm hover:bg-slate-50">
              <Wallet className="h-4 w-4" />Cash Session
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Bills" value={loading ? "…" : completedSales.length} icon="Receipt" tone="navy" />
        <StatCard label="Total Sales" value={loading ? "…" : currency(salesTotals.total)} icon="BadgeDollarSign" tone="gold" />
        <StatCard label="Cash" value={loading ? "…" : currency(salesTotals.cash)} icon="Banknote" tone="emerald" />
        <StatCard label="Card" value={loading ? "…" : currency(salesTotals.card)} icon="CreditCard" tone="sky" />
      </div>

      {(errors.sales || errors.summary || errors.settlements) && <div role="alert" className="bms-card mt-4 border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{[errors.sales, errors.summary, errors.settlements].filter(Boolean).join(" ")}</div>}

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-12">
        <section className="xl:col-span-4">
          <div className="bms-card h-full p-5">
            <h2 className="font-display text-lg font-semibold">Restaurant Sales</h2>
            <p className="mt-1 text-sm text-slate-500">Direct Restaurant POS sales for {dateLabel}.</p>
            <div className="mt-5 space-y-3">
              <SummaryTile label="Direct Restaurant Sales" value={loading ? "…" : currency(salesTotals.total)} />
              <SummaryTile label="Orders / Bills" value={loading ? "…" : completedSales.length} />
            </div>
          </div>
        </section>

        <section className="xl:col-span-8">
          <div className="bms-card h-full p-5">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div><h2 className="font-display text-lg font-semibold">Settlement Summary</h2><p className="text-sm text-slate-500">Transfers and payable for {dateLabel}.</p></div>
              <Link to={`/res-cashier/cash-settlements?section=settlement&businessDate=${selectedBusinessDate}`} className="text-sm font-medium text-navy-700 hover:underline">Cash &amp; Settlements →</Link>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className={`rounded-xl border p-4 ${pendingRequests.length ? "border-amber-200 bg-amber-50/70" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start justify-between gap-3"><div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">BAR → Restaurant</div><div className="mt-2 text-sm font-semibold text-slate-800">{pendingRequests.length ? `${pendingRequests.length} pending request${pendingRequests.length === 1 ? "" : "s"}` : "No pending requests"}</div></div>{pendingRequests.length ? <StatusBadge value="PENDING" /> : <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Clear</span>}</div>
                <div className="mt-3 flex items-end justify-between gap-2"><div><div className="text-xs text-slate-500">Pending amount</div><div className="font-display text-xl font-bold text-slate-900">{currency(pendingAmount)}</div></div><Link to={`/res-cashier/cash-settlements?section=history&businessDate=${selectedBusinessDate}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Review</Link></div>
                <div className="mt-2 text-xs text-slate-500">Received: {loading ? "…" : currency(settlementSummary?.receivedFromBar)}</div>
              </div>
              <div className={`rounded-xl border p-4 ${settlementSummary?.ownerSettlementAlreadyRequested ? "border-amber-200 bg-amber-50/70" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start justify-between gap-3"><div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Restaurant → Owner</div><div className="mt-2 text-sm font-semibold text-slate-800">{settlementSummary?.ownerSettlementAlreadyRequested ? `Pending ${settlementSummary?.existingOwnerSettlementStatus || "request"}` : "Available to settle"}</div></div>{settlementSummary?.ownerSettlementAlreadyRequested && <StatusBadge value={settlementSummary?.existingOwnerSettlementStatus || "PENDING"} />}</div>
                <div className="mt-3 flex items-end justify-between gap-2"><div><div className="text-xs text-slate-500">Available amount</div><div className="font-display text-xl font-bold text-slate-900">{loading ? "…" : currency(settlementSummary?.ownerPayable)}</div></div><Link to={`/res-cashier/cash-settlements?section=settlement&businessDate=${selectedBusinessDate}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">View Settlement</Link></div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="bms-card mt-6 p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div><h2 className="font-display text-lg font-semibold">Restaurant Product Sales Count</h2><p className="text-sm text-slate-500">Quantities sold through Restaurant POS on {dateLabel}.</p></div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"><PackageSearch className="h-3.5 w-3.5" />{productRows.length} products shown</div>
        </div>
        {productRows.length ? <ul className="mt-3 divide-y divide-slate-100">{productRows.map((item) => <li key={item.key} className="flex items-center justify-between gap-3 py-2.5"><span className="truncate text-sm font-medium text-slate-800">{item.name}</span><span className="rounded-lg bg-slate-50 px-3 py-1 text-sm font-bold text-slate-900">{item.quantity}</span></li>)}</ul> : <p className="mt-3 rounded-lg bg-slate-50 px-3 py-4 text-sm text-slate-500">{errors.sales ? "Product sales are unavailable." : "No Restaurant products sold for this date."}</p>}
      </section>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="bms-card p-5">
          <div className="flex items-center justify-between"><div><h2 className="font-display text-lg font-semibold">Restaurant Cash Session</h2><p className="text-xs text-slate-500">Current Restaurant session</p></div><StatusBadge value={session ? "OPEN" : "CLOSED"} /></div>
          {session ? <div className="mt-3 space-y-2 text-sm"><Row label="Opened at" value={dateTime(session.openedAt)} /><Row label="Opening cash" value={currency(session.openingCashAmount)} /><Row label="Cash sales so far" value={currency(session.cashSalesAmount)} /><Row label="Expected closing cash" value={session.expectedClosingCash == null ? "Not available" : currency(session.expectedClosingCash)} bold /><Link to="/res-cashier/cash-session" className="mt-2 inline-block text-sm text-navy-700 hover:underline">Close session →</Link></div> : <div className="mt-3 text-sm text-slate-500">No open Restaurant session. <Link to="/res-cashier/cash-session" className="text-navy-700 hover:underline">Start a session</Link>.</div>}
        </section>
        <section className="bms-card p-5">
          <div className="flex items-center justify-between gap-3"><div><h2 className="font-display text-lg font-semibold">Restaurant Orders</h2><p className="text-xs text-slate-500">Calendar date {dateLabel}</p></div><Link to="/res-cashier/orders" className="inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline">View Orders <ClipboardList className="h-4 w-4" /></Link></div>
          <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"><span className="text-sm text-slate-600">Orders on calendar date</span><strong className="font-display text-lg text-slate-900">{loading ? "…" : orderTotal}</strong></div>
          {errors.orders ? <p className="mt-3 text-sm text-rose-700">{errors.orders}</p> : orders.length ? <ul className="mt-2 divide-y divide-slate-100">{orders.slice(0, 3).map((order) => <li key={order.restaurantOrderId} className="flex items-center justify-between gap-3 py-2"><span className="truncate text-sm font-medium text-slate-800">{order.orderNumber || `Order ${order.restaurantOrderId}`}</span><StatusBadge value={order.orderState || "CURRENT"} /></li>)}</ul> : <p className="mt-3 text-sm text-slate-500">{loading ? "Loading orders…" : "No Restaurant Orders for this date."}</p>}
        </section>
      </div>

      <section className="bms-card mt-6 p-5">
        <div className="flex items-center justify-between gap-3"><h2 className="font-display text-lg font-semibold">Bills for Selected Date</h2><Link to="/res-cashier/my-sales" className="inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline">My Sales <ReceiptText className="h-4 w-4" /></Link></div>
        {errors.sales ? <p className="mt-3 text-sm text-rose-700">{errors.sales}</p> : completedSales.length ? <ul className="mt-2 divide-y divide-slate-100">{completedSales.slice(0, 5).map((sale) => <li key={sale.saleId}><Link to={`/res-cashier/sales/${sale.saleId}`} className="-mx-2 flex items-center justify-between gap-3 rounded px-2 py-2.5 hover:bg-slate-50/60"><div className="min-w-0"><div className="truncate font-medium text-slate-900">{sale.billNumber || `Sale ${sale.saleId}`}</div><div className="text-xs text-slate-500">{dateTime(sale.saleDateTime)} · {paymentMethods(sale.payments)}</div></div><div className="flex shrink-0 items-center gap-2"><StatusBadge value={sale.status} /><span className="w-28 text-right font-display font-semibold">{currency(sale.grandTotalAmount)}</span></div></Link></li>)}</ul> : <p className="mt-3 text-sm text-slate-500">{loading ? "Loading bills…" : "No bills for the selected business date."}</p>}
      </section>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm"><Link to="/res-cashier/my-sales" className="inline-flex items-center gap-2 text-navy-700 hover:underline"><ReceiptText className="h-4 w-4" />My Sales</Link><Link to="/res-cashier/orders" className="inline-flex items-center gap-2 text-navy-700 hover:underline"><ClipboardList className="h-4 w-4" />Restaurant Orders</Link><Link to={`/res-cashier/cash-settlements?section=settlement&businessDate=${selectedBusinessDate}`} className="inline-flex items-center gap-2 text-navy-700 hover:underline"><HandCoins className="h-4 w-4" />Cash &amp; Settlements</Link></div>
    </div>
  );
};

const paymentMethods = (payments) => Array.from(new Set((Array.isArray(payments) ? payments : []).map((payment) => payment.paymentMethod).filter(Boolean))).join(" + ") || "Payment recorded";

const SummaryTile = ({ label, value }) => <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div><div className="mt-2 font-display text-[22px] font-bold text-slate-900">{value}</div></div>;

const Row = ({ label, value, bold }) => <div className="flex items-center justify-between gap-3"><span className="text-slate-500">{label}</span><span className={bold ? "font-display font-semibold text-slate-900" : "text-slate-800"}>{value}</span></div>;
